import React from "react";
import { useTranslation } from "react-i18next";
import "../styleSheets/messageViewShell.css";
import "../styleSheets/commonElements.css";
import "../styleSheets/buttons.css";
import { capitalizeFirst, getFileTypeIcon } from "../utilities/utilities";
import { normalizeMessageBodyForRender } from "../utilities/messageBodyHtml";
import { TooltipButton } from "../utilities/ToolTipButton";

export type MessageViewAttachment = {
  id: string;
  fileName: string;
  fileType?: string | null;
  fileSize?: number | null;
  s3Key: string;
};

type Props = {
  variant: "legacy" | "inherited" | "sent" | "delivered";

  title: string;
  lastEditedLabel?: string | null;

  deliveryLabel: string; // e.g. "Scheduled to send", "Delivered on"
  deliveryValue: string; // already formatted for display

  recipientsLabel?: string; // e.g. "Recipients"
  recipientsValue?: string; // e.g. "Sarah Parker, ..."

  bodyHtml: string;
  bodyFooter?: React.ReactNode;
  extraDetails?: React.ReactNode;

  attachments: MessageViewAttachment[];
  attachmentsLabel?: string; // default "Attachments"
  showAttachmentsSection?: boolean;

  onBack: () => void;
  onEdit: () => void;
  editDisabled?: boolean;

  //Inherited Message Data
  fromName?: string;
  fromNameLabel?: string;
  fromEmail?: string | null;

  onPrintMessage?: () => void;
  onDownloadAttachments?: () => void;
  showDownloadAttachments?: boolean;
  downloadAttachmentsLabel?: string;
  actionsDisabled?: boolean;
  actionMessage?: string | null;
  actionWarning?: string | null;
  actionError?: string | null;
};

const MessageViewShell: React.FC<Props> = ({
  variant,
  title,
  lastEditedLabel,
  deliveryLabel,
  deliveryValue,
  recipientsLabel = "",
  recipientsValue = "",
  bodyHtml,
  bodyFooter = null,
  extraDetails = null,
  attachments,
  attachmentsLabel,
  showAttachmentsSection = true,
  onBack,
  onEdit,
  editDisabled = false,
  fromName = "",
  fromNameLabel = "",
  fromEmail = "",
  onPrintMessage,
  onDownloadAttachments,
  showDownloadAttachments = false,
  downloadAttachmentsLabel,
  actionsDisabled = false,
  actionMessage = null,
  actionWarning = null,
  actionError = null,
}) => {
  const { t } = useTranslation("common");
  const attachmentsLabelRender =
    attachmentsLabel ?? capitalizeFirst(t("file.attachments"));
  const inheritedSenderLabel =
    [fromName, fromEmail ? `(${fromEmail})` : ""].filter(Boolean).join(" ") ||
    fromEmail ||
    "";
  const showActions = !!onPrintMessage || (!!onDownloadAttachments && showDownloadAttachments);
  const normalizedBodyHtml = normalizeMessageBodyForRender(bodyHtml);

  return (
    <article className="message-view-page" aria-labelledby="message-view-title">
      {/* Top nav */}
      <header className="msg-topbar">
        <div className="msg-topbar__section">
          <div className="msg-topbar__container">
            <div className="msg-topbar__row">
              <button
                type="button"
                className="btn btn--gray btn--size-md btn--icon-left"
                onClick={onBack}
              >
                <div
                  className="btn__icon icon-chevron-left"
                  aria-hidden="true"
                />
                {t("common.back")}
              </button>

              <div className="msg-topbar__title">
                {variant === "legacy"
                  ? t("message.legacyMessage")
                  : variant === "delivered"
                    ? t("deliveredMessage.deliveredMessage")
                  : variant === "sent"
                    ? t("message.sentMessage")
                    : t("message.inheritedMessage")}
              </div>
              <div className="msg-topbar__actions">
                {variant !== "inherited" && variant !== "delivered" && (
                  <TooltipButton
                    label={t("message.editMessage")}
                    id="delete-recipient-btn"
                    tooltip={t("message.onScreenMessage.sentMessagesNoEdit")}
                    disabled={editDisabled}
                    onClick={onEdit}
                    buttonClassName="btn btn--white btn--size-md"
                    buttonIcon={false}
                  />
                )}
              </div>
            </div>

            {/* Small-screen title (same pattern as Recipient) */}
            <div className="msg-topbar__titleBelow">
              {variant === "legacy"
                ? t("message.legacyMessage")
                : variant === "delivered"
                  ? t("deliveredMessage.deliveredMessage")
                : variant === "sent"
                  ? t("message.sentMessage")
                  : t("message.inheritedMessage")}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="msg-content msg-content--view">
        <div className="msg-card msg-card--view">
          {/* Header summary */}
          <header className="msg-summary">
            <div className="msg-summary__titleBlock">
              <h1 id="message-view-title" className="msg-summary__title">{title}</h1>
              {lastEditedLabel ? (
                <div className="msg-subtle">
                  {t("common.lastEditedOn") + " "} {lastEditedLabel}
                </div>
              ) : null}
            </div>

            <div className="msg-meta">
              <div className="msg-meta__block">
                <div className="msg-meta__label">{deliveryLabel}</div>
                <div className="msg-meta__value">{deliveryValue}</div>
              </div>
              {variant === "inherited" && (
                <div className="msg-meta__block">
                  <div className="msg-meta__label">{fromNameLabel}</div>
                  <div className="msg-meta__value">{inheritedSenderLabel}</div>
                </div>
              )}
              {variant !== "inherited" && variant !== "delivered" && (
                <div className="msg-meta__block">
                  <div className="msg-meta__label">{recipientsLabel}</div>
                  <div className="msg-meta__value">{recipientsValue}</div>
                </div>
              )}
            </div>
          </header>

          {/* Body */}
          <section className="msg-body" aria-label={t("messageView.body")}>
            <div
              className="msg-body__html"
              data-testid="message-body-html"
              dangerouslySetInnerHTML={{ __html: normalizedBodyHtml }}
            />
            {bodyFooter ? (
              <div className="msg-body__footer">{bodyFooter}</div>
            ) : null}
          </section>

          {extraDetails ? (
            <section
              className="msg-extra-details"
              aria-label={t("messageView.details")}
            >
              {extraDetails}
            </section>
          ) : null}

          {/* Attachments */}
          {showAttachmentsSection ? (
            <section
              className="msg-attachments"
              aria-labelledby="message-attachments-title"
            >
              <div className="msg-attachments__header">
                <h2 id="message-attachments-title" className="msg-attachments__title">
                  {attachmentsLabelRender}
                </h2>
                <div className="msg-subtle">
                  {attachments.length > 0 &&
                    `${attachments.length} ${attachments.length > 1 ? t("file.attachments") : t("file.attachment")}`}
                </div>
              </div>

              {attachments.length === 0 ? (
                <div className="msg-empty">
                  {t("file.onScreenMessage.noAttachments")}
                </div>
              ) : (
                <div className="msg-attachments__row">
                  {attachments.map((a) => {
                    const Icon = getFileTypeIcon(a?.fileType || "");
                    return (
                      <div key={a.id} className="msg-chip btn--dark-green">
                        <Icon className="msg-chip__icon" aria-hidden="true" />
                        <span className="msg-chip__text">{a.fileName}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {showActions ? (
                <div className="msg-actions">
                  <div className="msg-actions__buttons">
                    {onPrintMessage ? (
                      <button
                        type="button"
                        className="btn btn--white btn--size-md"
                        onClick={onPrintMessage}
                        disabled={actionsDisabled}
                      >
                        {t("inheritedMessage.actionLabel.printMessage")}
                      </button>
                    ) : null}

                    {onDownloadAttachments && showDownloadAttachments ? (
                      <button
                        type="button"
                        className="btn btn--orange btn--size-md"
                        onClick={onDownloadAttachments}
                        disabled={actionsDisabled}
                      >
                        {downloadAttachmentsLabel ??
                          t("inheritedMessage.actionLabel.downloadAttachments")}
                      </button>
                    ) : null}
                  </div>

                  <div className="msg-actions__feedback" aria-live="polite">
                    {actionMessage ? (
                      <div className="msg-actions__message">{actionMessage}</div>
                    ) : null}
                    {actionWarning ? (
                      <div className="msg-actions__warning">{actionWarning}</div>
                    ) : null}
                    {actionError ? (
                      <div className="msg-actions__error">{actionError}</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default MessageViewShell;
